import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { useEffect, type ReactNode } from "react";
import { AuthProvider, useAuth } from "./lib/auth-context.js";
import { PreferencesProvider } from "./lib/preferences.js";
import { usePresenceTracker } from "./lib/use-presence-tracker.js";
import { ProtectedRoute } from "./components/protected-route.js";
import { PublicOnlyRoute } from "./components/public-only-route.js";
import { AppShell } from "./components/app-shell.js";
import { SignInPage } from "./pages/sign-in.js";
import { SignUpPage } from "./pages/sign-up.js";
import { DashboardPage } from "./pages/dashboard.js";
import { HouseholdSettingsPage } from "./pages/household-settings.js";
import { ChartPage } from "./pages/chart.js";
import { WatchlistsPage } from "./pages/watchlists.js";
import { AccountsPage } from "./pages/accounts.js";
import { ImportPage } from "./pages/import.js";
import { AlertsPage } from "./pages/alerts.js";
import { JournalPage } from "./pages/journal.js";
import { MessagesPage } from "./pages/messages.js";
import { FamilyPage } from "./pages/family.js";
import { FamilyMemberAllowancePage } from "./pages/family-member-allowance.js";
import { FamilyMemberBucketsPage } from "./pages/family-member-buckets.js";
import { BillsPage } from "./pages/bills.js";
import { SpendingPage } from "./pages/spending.js";
import { IncomePage } from "./pages/income.js";
import { EquityGrantsPage } from "./pages/equity-grants.js";
import { HousingPage } from "./pages/housing.js";
import { CollegePlansPage } from "./pages/college-plans.js";
import { ScenariosPage } from "./pages/scenarios.js";
import { TaxPage } from "./pages/tax/index.js";
import { TaxQuarterlyPage } from "./pages/tax/quarterly.js";
import { TaxCapitalGainsPage } from "./pages/tax/capital-gains.js";
import { TaxRothLadderPage } from "./pages/tax/roth-ladder.js";
import { TaxSeppPage } from "./pages/tax/sepp.js";
import { TaxAcaPlannerPage } from "./pages/tax/aca-planner.js";
import { TaxDocumentsPage } from "./pages/tax/documents.js";
import { AiPage } from "./pages/ai.js";
import { SettingsAiPage } from "./pages/settings-ai.js";
import { IntegrationsPage } from "./pages/integrations.js";
import { IntegrationsStagingPage } from "./pages/integrations-staging.js";
import { CreditPage } from "./pages/credit.js";
import { CardsPage } from "./pages/cards.js";
import { BankingIndexPage } from "./pages/banking/index.js";
import { BankingHysaPage } from "./pages/banking/hysa.js";
import { BankingCdLadderPage } from "./pages/banking/cd-ladder.js";
import { BankingIBondsPage } from "./pages/banking/i-bonds.js";
import { EstatePage } from "./pages/estate.js";
import { EstateLetterOfIntentPage } from "./pages/estate/letter-of-intent.js";
import { EstateDigitalAssetsPage } from "./pages/estate/digital-assets.js";
import { EstateBurialWishesPage } from "./pages/estate/burial-wishes.js";
import { InsurancePage } from "./pages/insurance.js";
import { DocumentsPage } from "./pages/documents.js";
import { CharityPage } from "./pages/charity.js";
// Concept-preview route — renders docs/family-finance-app.jsx in-app for
// IA / design review. Not in main nav; reach it via /family-finance-app.
import FamilyFinanceAppConcept from "./pages/family-finance-app.js";
import { PortfolioPage } from "./pages/portfolio.js";
import { PortfolioRebalancePage } from "./pages/portfolio/rebalance.js";
import { PortfolioStressTestPage } from "./pages/portfolio/stress-test.js";
import { FireContributionPriorityPage } from "./pages/fire/contribution-priority.js";
import { FireHubPage } from "./pages/fire/index.js";
import { FireNumbersPage } from "./pages/fire/numbers.js";
import { FireWithdrawalPage } from "./pages/fire/withdrawal.js";
import { FireSavingRatePage } from "./pages/fire/saving-rate.js";
import { FireSocialSecurityPage } from "./pages/fire/social-security.js";
import { FireGeographicArbitragePage } from "./pages/fire/geographic-arbitrage.js";
import { SettingsEmailsPage } from "./pages/settings-emails.js";
import { NotFoundPage } from "./pages/not-found.js";

/**
 * Routing tree. Protected routes wrap their content in <AppShell> so the
 * sidebar + identity footer surrounds every authed page. Sign-in / sign-up
 * sit outside the shell — pre-auth, no household identity yet.
 *
 * The presence-tracker only fires when authenticated; gated via the
 * `enabled` flag so unauth landing pages don't ping the API.
 */
/**
 * Resets window scroll to the top whenever pathname changes (excluding
 * hash-only fragment navigation, which the dashboard uses for in-page
 * `#fire` / `#stress-test` anchors). Phase 9 fix: navigating from a
 * scrolled-down /dashboard to /chart/AAPL was preserving the scroll
 * position; React Router v7 doesn't restore by default outside the
 * data-router APIs.
 */
function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname]);
  return null;
}

/**
 * Phase 13.5 (IA implementation) — redirect deprecated hash-anchor URLs
 * to their canonical /fire/* sub-routes. Pre-13.5 the sidebar exposed
 * `/dashboard#fire` and `/dashboard#stress-test`; the IA review locked
 * those promotions to `/fire/numbers` and `/fire/withdrawal`. Anyone
 * who shared an external link or has a bookmark to the old URL lands
 * on /dashboard, then this component kicks in once the SPA mounts and
 * navigates them to the canonical route. Brief flash of /dashboard
 * before the redirect; acceptable for a deprecated path.
 */
function HashAnchorRedirector() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (pathname !== "/dashboard") return;
    if (hash === "#fire") void navigate("/fire/numbers", { replace: true });
    else if (hash === "#stress-test") void navigate("/fire/withdrawal", { replace: true });
  }, [pathname, hash, navigate]);
  return null;
}

function AppRoutes() {
  const { status } = useAuth();
  usePresenceTracker(status === "auth");
  return (
    <>
    <ScrollToTopOnRouteChange />
    <HashAnchorRedirector />
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/signin"
        element={
          <PublicOnlyRoute>
            <SignInPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicOnlyRoute>
            <SignUpPage />
          </PublicOnlyRoute>
        }
      />
      <Route path="/dashboard" element={<Shelled><DashboardPage /></Shelled>} />
      <Route path="/settings/household" element={<Shelled><HouseholdSettingsPage /></Shelled>} />
      <Route path="/settings/emails" element={<Shelled><SettingsEmailsPage /></Shelled>} />
      <Route path="/watchlists" element={<Shelled><WatchlistsPage /></Shelled>} />
      <Route path="/chart/:ticker" element={<Shelled><ChartPage /></Shelled>} />
      <Route path="/alerts" element={<Shelled><AlertsPage /></Shelled>} />
      <Route path="/journal" element={<Shelled><JournalPage /></Shelled>} />
      <Route path="/accounts" element={<Shelled><AccountsPage /></Shelled>} />
      <Route path="/import" element={<Shelled><ImportPage /></Shelled>} />
      <Route path="/messages" element={<Shelled><MessagesPage /></Shelled>} />
      <Route path="/family" element={<Shelled><FamilyPage /></Shelled>} />
      <Route path="/family/:memberId/allowance" element={<Shelled><FamilyMemberAllowancePage /></Shelled>} />
      <Route path="/family/:memberId/buckets" element={<Shelled><FamilyMemberBucketsPage /></Shelled>} />
      <Route path="/bills" element={<Shelled><BillsPage /></Shelled>} />
      <Route path="/spending" element={<Shelled><SpendingPage /></Shelled>} />
      <Route path="/income" element={<Shelled><IncomePage /></Shelled>} />
      <Route path="/equity-grants" element={<Shelled><EquityGrantsPage /></Shelled>} />
      <Route path="/housing" element={<Shelled><HousingPage /></Shelled>} />
      <Route path="/college-plans" element={<Shelled><CollegePlansPage /></Shelled>} />
      <Route path="/scenarios" element={<Shelled><ScenariosPage /></Shelled>} />
      <Route path="/tax" element={<Shelled><TaxPage /></Shelled>} />
      <Route path="/tax/quarterly" element={<Shelled><TaxQuarterlyPage /></Shelled>} />
      <Route path="/tax/capital-gains" element={<Shelled><TaxCapitalGainsPage /></Shelled>} />
      <Route path="/tax/roth-ladder" element={<Shelled><TaxRothLadderPage /></Shelled>} />
      <Route path="/tax/sepp" element={<Shelled><TaxSeppPage /></Shelled>} />
      <Route path="/tax/aca-planner" element={<Shelled><TaxAcaPlannerPage /></Shelled>} />
      <Route path="/tax/documents" element={<Shelled><TaxDocumentsPage /></Shelled>} />
      <Route path="/ai" element={<Shelled><AiPage /></Shelled>} />
      <Route path="/settings/ai" element={<Shelled><SettingsAiPage /></Shelled>} />
      <Route path="/integrations" element={<Shelled><IntegrationsPage /></Shelled>} />
      <Route path="/integrations/staging" element={<Shelled><IntegrationsStagingPage /></Shelled>} />
      <Route path="/credit" element={<Shelled><CreditPage /></Shelled>} />
      <Route path="/cards" element={<Shelled><CardsPage /></Shelled>} />
      <Route path="/banking" element={<Shelled><BankingIndexPage /></Shelled>} />
      <Route path="/banking/hysa" element={<Shelled><BankingHysaPage /></Shelled>} />
      <Route path="/banking/cd-ladder" element={<Shelled><BankingCdLadderPage /></Shelled>} />
      <Route path="/banking/i-bonds" element={<Shelled><BankingIBondsPage /></Shelled>} />
      <Route path="/estate" element={<Shelled><EstatePage /></Shelled>} />
      <Route path="/estate/letter-of-intent" element={<Shelled><EstateLetterOfIntentPage /></Shelled>} />
      <Route path="/estate/digital-assets" element={<Shelled><EstateDigitalAssetsPage /></Shelled>} />
      <Route path="/estate/burial-wishes" element={<Shelled><EstateBurialWishesPage /></Shelled>} />
      <Route path="/insurance" element={<Shelled><InsurancePage /></Shelled>} />
      <Route path="/documents" element={<Shelled><DocumentsPage /></Shelled>} />
      <Route path="/charity" element={<Shelled><CharityPage /></Shelled>} />
      {/* Concept preview — wrapped in ProtectedRoute (auth required) but
          NOT in AppShell so the mockup's own dark sidebar/header doesn't
          fight with Hearth's. Source: docs/family-finance-app.jsx. */}
      <Route
        path="/family-finance-app"
        element={
          <ProtectedRoute>
            <FamilyFinanceAppConcept />
          </ProtectedRoute>
        }
      />
      <Route path="/portfolio" element={<Shelled><PortfolioPage /></Shelled>} />
      <Route path="/portfolio/rebalance" element={<Shelled><PortfolioRebalancePage /></Shelled>} />
      <Route path="/portfolio/stress-test" element={<Shelled><PortfolioStressTestPage /></Shelled>} />
      {/* Phase 13.5: FIRE elevation. /fire is the marketing-anchor hub;
          sub-routes extract individual tiles previously stacked on
          /dashboard. Hash-anchor URLs (`/dashboard#fire`,
          `/dashboard#stress-test`) redirect via `<HashAnchorRedirector />`
          mounted in AppRoutes (see Cleave 3 of Phase 13.5). */}
      <Route path="/fire" element={<Shelled><FireHubPage /></Shelled>} />
      <Route path="/fire/numbers" element={<Shelled><FireNumbersPage /></Shelled>} />
      <Route path="/fire/withdrawal" element={<Shelled><FireWithdrawalPage /></Shelled>} />
      <Route path="/fire/saving-rate" element={<Shelled><FireSavingRatePage /></Shelled>} />
      <Route path="/fire/contribution-priority" element={<Shelled><FireContributionPriorityPage /></Shelled>} />
      <Route path="/fire/social-security" element={<Shelled><FireSocialSecurityPage /></Shelled>} />
      <Route path="/fire/geographic-arbitrage" element={<Shelled><FireGeographicArbitragePage /></Shelled>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </>
  );
}

// Auth-gated shell wrapper — every protected page goes through this.
function Shelled({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

export function App() {
  return (
    <AuthProvider>
      <PreferencesProvider>
        <AppRoutes />
      </PreferencesProvider>
    </AuthProvider>
  );
}
